import React, { useEffect, useState } from 'react';
import { useTranslation, WithTranslation } from 'react-i18next';
import { QRCodeSVG }   from 'qrcode.react';
import ApiService from '../../services/api.service';
import CSS from 'csstype';
import { RouteComponentProps } from 'react-router';
import { GlobalState } from '../../models/global-state';
import OrderState from '../../models/order.state';
import { Configuration } from '../../models/configuration';

interface QrCodePopupProps {
    onClose: () => void;
    configurations: Configuration;
    isPreview: boolean;
}

interface AgeConfirmationResponse {
    code: string;
    validTillUtc: string; // ISO string
}

type StateProps =
    GlobalState & OrderState & WithTranslation &
    RouteComponentProps<{}>;



const AgeConfirmPopup: React.FC<QrCodePopupProps> = ({ onClose, configurations, isPreview }) => {
    const [qrData, setQrData] = useState<AgeConfirmationResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();
    const buttonStyles: CSS.Properties = {
        backgroundColor: configurations.buttonBackgroundColor,
        color: configurations.buttonFontColor
    }
    const overlayStyle: CSS.Properties = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    };
    const contentStyle: CSS.Properties = {
        backgroundColor: '#ffffff',
        border: '2px solid #ccc',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        maxWidth: '420px',
        width: '90%',
        textAlign: 'center'
    };
    useEffect(() => {
        let interval: NodeJS.Timeout;
        ApiService.getAgeConfirmCode().then(res => {
            setIsLoading(false);
            if (res && res.code && res.validTillUtc) {
                setQrData(res);
                
                // Start interval to check expiration
                interval = setInterval(() => {
                    const now = new Date();
                    const validTill = new Date(res.validTillUtc);
                    if (now > validTill) {
                        clearInterval(interval);
                        onClose();
                    }
                }, 1000);
            }
        });
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [onClose]);

    const formatValidTill = (validTill: string) => {
        const date = new Date(validTill);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="qr-popup-overlay" style={overlayStyle}>
            <div className="qr-popup-content" style={contentStyle}>
                {isLoading ? (
                    <div>Loading...</div>
                ) : !qrData && !isPreview ? (
                    <>
                        <div style={{ marginBottom: '18px', fontSize: '20px' }}>
                            {t('WebLinkCollectScreen_AgeConfirmationQRPopupUnavailable')}
                        </div>
                        <button onClick={onClose} className="main-button" style={buttonStyles}>{t('WebLinkCollectScreen_AgeConfirmationQRPopupCloseButtonText')}</button>
                    </>
                ) : (
                    <>
                        <div style={{ fontWeight: 'bold', fontSize: '22px', marginBottom: '30px' }}>
                            {t('WebLinkCollectScreen_AgeConfirmationQRPopupTitle')}
                        </div>
                        <div style={{ margin: '20px' }}>
                            <QRCodeSVG
                                value={qrData?.code ?? 'preview'}
                                title={qrData?.code ?? 'preview'}
                                size={250}
                            />
                        </div>
                        <div style={{ fontSize: '22px', marginTop: '10px', fontWeight: 'bold' }}>
                            {qrData?.code ?? '000-000'}
                        </div>
                        <div style={{ marginBottom: '18px', fontSize: '20px' }}>
                            {t('WebLinkCollectScreen_AgeConfirmationQRPopupValidTime', { validTill: formatValidTill(qrData?.validTillUtc ?? new Date().toISOString()) })
                                .replace('[validTill]', formatValidTill(qrData?.validTillUtc ?? new Date().toISOString()))}
                        </div>
                        <button onClick={onClose} className="main-button" style={buttonStyles}>{t('WebLinkCollectScreen_AgeConfirmationQRPopupCloseButtonText')}</button>
                    </>
                )}
            </div>
        </div>
    );
};

export default AgeConfirmPopup;